import type { AiCompanionAttendanceProposal } from "@simbelapp/order-sdk";
import type { IAttendanceMatchedSession } from "~/utils/interfaces/attendance-review-interfaces";

/** Spelled out rather than numeric: the design dates the session the way a sheet header would. */
const MONTH_NAMES = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

const DAY_MS = 24 * 60 * 60 * 1000;

/** A date reduced to the calendar day it names, with a stamp to order and subtract two of them by. */
type CalendarDay = { day: number; month: number; year: number; stamp: number };

/**
 * The day an API date names, read off its digits rather than through the local timezone: a session
 * stored at midnight UTC is the day before for anyone west of Greenwich.
 */
function calendarDayOf(value: string | Date | null | undefined): CalendarDay | null {
  if (!value) return null;

  const iso = value instanceof Date ? (Number.isNaN(value.getTime()) ? "" : value.toISOString()) : value;
  const [, year, month, day] = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) ?? [];
  if (!year || !month || !day) return null;

  const calendarDay = {
    day: Number(day),
    month: Number(month),
    year: Number(year),
    stamp: Date.UTC(Number(year), Number(month) - 1, Number(day)),
  };

  // Well-formed digits are not a real day: "2026-13-01" would name no month and "2026-02-30" would
  // roll over to March while still reading "30 février". Only a date that survives the round-trip
  // through UTC is one the label can name.
  const stamped = new Date(calendarDay.stamp);
  if (
    stamped.getUTCFullYear() !== calendarDay.year ||
    stamped.getUTCMonth() + 1 !== calendarDay.month ||
    stamped.getUTCDate() !== calendarDay.day
  ) {
    return null;
  }

  return calendarDay;
}

/** "12 mars", with the year appended when the session did not happen in the current one. */
function dayLabel(day: CalendarDay, withYear: boolean): string {
  const label = `${day.day === 1 ? "1er" : day.day} ${MONTH_NAMES[day.month - 1]}`;

  return withYear ? `${label} ${day.year}` : label;
}

/**
 * When the session took place, as the design writes it: "12 mars", "12 et 13 mars" for the two-day
 * session most sheets cover, "du 12 au 16 mars" beyond that. The year only shows when it is not the
 * current one — attendance is normally declared in the year it was signed.
 */
export function attendanceSessionDatesLabel(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  currentYear: number = new Date().getFullYear(),
): string | null {
  // A session missing one of its bounds is still worth dating from the other.
  const from = calendarDayOf(startDate) ?? calendarDayOf(endDate);
  const to = calendarDayOf(endDate) ?? calendarDayOf(startDate);
  if (!from || !to) return null;

  // Ordered rather than trusted: an inverted range would otherwise read backwards.
  const isOrdered = from.stamp <= to.stamp;
  const first = isOrdered ? from : to;
  const last = isOrdered ? to : from;
  const withYear = first.year !== currentYear || last.year !== currentYear;

  if (first.stamp === last.stamp) return dayLabel(first, withYear);
  // Consecutive days read as a pair, but only inside one month: "31 et 1 avril" would be nonsense.
  if (last.stamp - first.stamp === DAY_MS && first.month === last.month) {
    return `${first.day} et ${dayLabel(last, withYear)}`;
  }
  if (first.month === last.month && first.year === last.year) return `du ${first.day} au ${dayLabel(last, withYear)}`;

  return `du ${dayLabel(first, withYear)} au ${dayLabel(last, withYear)}`;
}

/**
 * The session the sheet was matched to, named above the review table: "Session EXCEL AVANCÉ · 12 et
 * 13 mars". Read off the proposal, which names the session it matched — `null` until it lands, so
 * the review shows no empty line while the analysis is still running.
 */
export function attendanceSessionLabel(proposal: AiCompanionAttendanceProposal | null): string | null {
  if (!proposal) return null;

  const trainingTitle = proposal.trainingTitle?.trim();
  const dates = attendanceSessionDatesLabel(proposal.startDate, proposal.endDate);
  if (!trainingTitle && !dates) return null;

  // A session carrying no training has no name of its own to borrow: its dates say which one it is.
  return [trainingTitle ? `Session ${trainingTitle}` : "Session", dates].filter(Boolean).join(" · ");
}

/** A session the proposal could not name is still a session: the picker words it the same way. */
const MATCHED_SESSION_FALLBACK_TITLE = "Formation";

/**
 * The session the sheet was matched to, for the card the conversation asks the admin to check
 * before the attendance opens. Detection can settle on a session without ever asking — one
 * candidate, or the page the sheet was dropped on — so this card is the only place that session is
 * named, and « Ouvrir les présences pré-remplies » is the only thing that acts on it.
 */
export function attendanceMatchedSessionOf(
  proposal: AiCompanionAttendanceProposal | null,
): IAttendanceMatchedSession | null {
  if (!proposal) return null;

  const title = proposal.trainingTitle?.trim();
  const dates = attendanceSessionDatesLabel(proposal.startDate, proposal.endDate);
  /*
   * Neither a training nor dates: the card would read "Formation" and check nothing at all. The
   * session is read through two left joins and falls back to empty dates, so a session carrying no
   * session item comes back nameless — the answer then keeps its CTA alone rather than pretending.
   */
  if (!title && !dates) return null;

  return { title: title || MATCHED_SESSION_FALLBACK_TITLE, dates };
}
